import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import MovieCreationRoundedIcon from '@mui/icons-material/MovieCreationRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded';
import { useTheme } from '../../../ThemeContext';

// Stills from assets for generated storyboard plates
import sceneCyber1 from '../../../assets/ai_shot_1.png';
import sceneCyber2 from '../../../assets/ai_shot_2.png';
import sceneCyber3 from '../../../assets/ai_shot_3.png';

import sceneNoir1 from '../../../assets/ChatGPT Image Aug 24, 2026, 01_52_21 PM.png';
import sceneNoir2 from '../../../assets/ChatGPT Image Aug 24, 2026, 01_54_39 PM.png';
import sceneNoir3 from '../../../assets/ChatGPT Image Aug 24, 2026, 02_00_10 PM.png';

import sceneSpace1 from '../../../assets/ChatGPT Image Aug 24, 2026, 01_49_52 PM.png';
import sceneSpace2 from '../../../assets/ChatGPT Image Aug 24, 2026, 01_57_04 PM.png';
import sceneSpace3 from '../../../assets/06_ai_filters.png';

const STORYBOARD_PROMPTS = [
  {
    id: 'cyberpunk',
    title: '🌆 Neo-Tokyo Cyberpunk',
    genre: 'Sci-Fi Action • Anamorphic',
    color: '#06b6d4',
    promptText: 'Neon-drenched rain alleyway, cybernetic operative scanning hologram evidence, optical violet reflections, 35mm anamorphic.',
    shots: [
      {
        plateId: 0,
        number: 'SHOT 01 • EXT. SHINJUKU ALLEY - NIGHT',
        framing: 'Establishing Wide • 24mm Anamorphic',
        movement: 'Slow Forward Dolly',
        lighting: 'Cyan Neon Key & Amber Spill',
        image: sceneCyber1,
        scriptNote: 'Rain pours over steaming subway grates. Holographic billboards pulse with corporate telemetry.',
        aspect: '2.39:1',
      },
      {
        plateId: 1,
        number: 'SHOT 02 • MED. TWO-SHOT - COVERAGE',
        framing: 'Medium Profile • 50mm T1.3',
        movement: 'Handheld Tension Drift',
        lighting: 'Chiaroscuro Silhouette',
        image: sceneCyber2,
        scriptNote: 'KIRA raises her optic scanner. "The neural signature matches the blackout protocol."',
        aspect: '2.39:1',
      },
      {
        plateId: 2,
        number: 'SHOT 03 • EXTREME CLOSE-UP - CLIMAX',
        framing: 'Close-Up • 85mm Prime f/1.4',
        movement: 'Static Punch-In',
        lighting: 'Ultraviolet Edge Rim',
        image: sceneCyber3,
        scriptNote: 'Her iris mechanically recalibrates as the encrypted memory chip clicks into place.',
        aspect: '2.39:1',
      },
    ],
  },
  {
    id: 'noir',
    title: '🕵️ 1920s Noir Detective',
    genre: 'Vintage Thriller • Low-Key',
    color: '#f59e0b',
    promptText: 'Venetian blinds casting stark geometric shadows over mahogany desk, vintage detective examining film negative, 35mm Tri-X grain.',
    shots: [
      {
        plateId: 0,
        number: 'SHOT 01 • INT. DETECTIVE OFFICE - DUSK',
        framing: 'Wide Master • 28mm Vintage Prime',
        movement: 'Low Angle Tilt Up',
        lighting: 'Venetian Blind Slices',
        image: sceneNoir1,
        scriptNote: 'Smoke curls from an untouched glass. Streetlamp shadows stretch across the wooden floor.',
        aspect: '2.39:1',
      },
      {
        plateId: 1,
        number: 'SHOT 02 • OVER-THE-SHOULDER - CONFRONTATION',
        framing: 'OTS Medium • 50mm Cooke Lens',
        movement: 'Subtle Whip Pan',
        lighting: 'Tungsten Practical Lamp',
        image: sceneNoir2,
        scriptNote: 'A mysterious silhouette stands framed by the frosted glass door.',
        aspect: '2.39:1',
      },
      {
        plateId: 2,
        number: 'SHOT 03 • MACRO INSERT - EVIDENCE',
        framing: 'Macro 90mm T2.8',
        movement: 'Rack Focus from Glass to Letter',
        lighting: 'Hard Specular Reflection',
        image: sceneNoir3,
        scriptNote: 'The wax seal bears the emblem of the missing industrialist.',
        aspect: '2.39:1',
      },
    ],
  },
  {
    id: 'space',
    title: '🚀 Deep Space Odyssey',
    genre: 'Cosmic Hard Sci-Fi • 70mm',
    color: '#8b5cf6',
    promptText: 'Orbital centrifuge station entering shadow of Jupiter, solitary astronaut tethered outside solar array, 70mm IMAX scale.',
    shots: [
      {
        plateId: 0,
        number: 'SHOT 01 • EXT. ORBITAL RING - DAWN',
        framing: 'Ultra-Wide • 14mm Cosmic Scale',
        movement: 'Zero-G Gliding Boom',
        lighting: 'Harsh Direct Sunlight & Earthshine',
        image: sceneSpace1,
        scriptNote: 'Solar arrays slowly rotate as the colossal planetary ring fills the lower third.',
        aspect: '2.39:1',
      },
      {
        plateId: 1,
        number: 'SHOT 02 • INT. AIRLOCK CORRIDOR',
        framing: 'Mid-Shot • 35mm Spherical',
        movement: 'Tracking Steadicam',
        lighting: 'Pulsing Emergency Amber',
        image: sceneSpace2,
        scriptNote: 'COMMANDER VANCE secures his helmet latch. "Pressure verified. Commencing egress."',
        aspect: '2.39:1',
      },
      {
        plateId: 2,
        number: 'SHOT 03 • CLOSE-UP - VISOR REFLECTION',
        framing: 'Helmet Macro • 65mm Prime',
        movement: 'Slow Breathing Pulse',
        lighting: 'Cyan HUD Projection Spill',
        image: sceneSpace3,
        scriptNote: 'The swirling aurora of the gas giant reflects in the gold-tinted visor.',
        aspect: '2.39:1',
      },
    ],
  },
];

/**
 * Interactive Script-to-Storyboard Live Diffusion Generator for Filmhook AI
 */
export const FilmhookStoryboardGenerator = ({ onSelectShot, activeShotIdx }) => {
  const { isDarkMode } = useTheme();
  const [selectedPromptId, setSelectedPromptId] = useState('cyberpunk');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(100);
  const [isExported, setIsExported] = useState(false);
  const [selectedShotPlate, setSelectedShotPlate] = useState(0);

  const activePrompt = STORYBOARD_PROMPTS.find((p) => p.id === selectedPromptId) || STORYBOARD_PROMPTS[0];

  const handleSelectPrompt = (promptId) => {
    if (promptId === selectedPromptId) return;
    setSelectedPromptId(promptId);
    setIsGenerating(true);
    setGenerationProgress(0);

    let current = 0;
    const interval = setInterval(() => {
      current += 15;
      if (current >= 100) {
        setGenerationProgress(100);
        setIsGenerating(false);
        clearInterval(interval);
      } else {
        setGenerationProgress(current);
      }
    }, 90);
  };

  const handleRegenerate = () => {
    setIsGenerating(true);
    setGenerationProgress(0);
    let current = 0;
    const interval = setInterval(() => {
      current += 20;
      if (current >= 100) {
        setGenerationProgress(100);
        setIsGenerating(false);
        clearInterval(interval);
      } else {
        setGenerationProgress(current);
      }
    }, 80);
  };

  const handleExportPDF = () => {
    setIsExported(true);
    setTimeout(() => setIsExported(false), 2400);
  };

  const handleShotClick = (idx) => {
    setSelectedShotPlate(idx);
    if (onSelectShot) {
      onSelectShot(idx);
    }
  };

  // Sync with parent activeShotIdx if provided
  useEffect(() => {
    if (activeShotIdx !== undefined && activeShotIdx < 3) {
      setSelectedShotPlate(activeShotIdx);
    }
  }, [activeShotIdx]);

  return (
    <Box
      sx={{
        width: '100%',
        mt: 4,
        pt: 3,
        borderTop: isDarkMode
          ? '1px solid rgba(6, 182, 212, 0.25)'
          : '1px solid rgba(6, 182, 212, 0.15)',
      }}
    >
      <Box
        sx={{
          borderRadius: '20px',
          background: isDarkMode
            ? 'linear-gradient(180deg, #050d1a 0%, #030812 100%)'
            : 'linear-gradient(180deg, #f0fdfa 0%, #ecfeff 100%)',
          border: isDarkMode
            ? '1.5px solid rgba(6, 182, 212, 0.35)'
            : '1.5px solid rgba(6, 182, 212, 0.25)',
          boxShadow: isDarkMode
            ? '0 18px 48px rgba(0, 0, 0, 0.65), 0 0 35px rgba(6, 182, 212, 0.15)'
            : '0 16px 36px rgba(15, 23, 42, 0.08)',
          p: { xs: 2, sm: 2.5, md: 3 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* ══════════════════════════════════════════════════════════
            1. HEADER TOOLBAR & PROMPT PRESET CHIPS
            ══════════════════════════════════════════════════════════ */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                boxShadow: '0 4px 14px rgba(6, 182, 212, 0.4)',
              }}
            >
              <MovieCreationRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={900}
                  sx={{ color: isDarkMode ? '#ffffff' : '#0f172a', fontSize: '0.98rem' }}
                >
                  Neural Script-to-Storyboard Generator
                </Typography>
                <Chip
                  label="Diffusion v2.4"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(6, 182, 212, 0.15)',
                    color: '#06b6d4',
                    fontWeight: 800,
                    fontSize: '0.66rem',
                    border: '1px solid rgba(6, 182, 212, 0.3)',
                    height: 20,
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.74rem' }}
              >
                Click cinematic prompt styles to synthesize 3 camera angle storyboard keyframe plates
              </Typography>
            </Box>
          </Box>

          {/* Action Buttons: Regenerate & Export */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              onClick={handleRegenerate}
              disabled={isGenerating}
              startIcon={<RefreshRoundedIcon sx={{ animation: isGenerating ? 'spin 1s linear infinite' : 'none' }} />}
              sx={{
                bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0',
                color: isDarkMode ? '#e2e8f0' : '#1e293b',
                fontWeight: 800,
                fontSize: '0.74rem',
                borderRadius: '12px',
                px: 1.6,
                py: 0.6,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.14)' : '#cbd5e1',
                },
              }}
            >
              {isGenerating ? 'Synthesizing...' : 'Regenerate Shots'}
            </Button>

            <Button
              size="small"
              onClick={handleExportPDF}
              startIcon={isExported ? <CheckRoundedIcon sx={{ color: '#10b981' }} /> : <DownloadRoundedIcon />}
              sx={{
                background: 'linear-gradient(90deg, #06b6d4 0%, #3b82f6 100%)',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '0.74rem',
                borderRadius: '12px',
                px: 1.8,
                py: 0.6,
                textTransform: 'none',
                boxShadow: '0 4px 14px rgba(6, 182, 212, 0.35)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #0891b2 0%, #2563eb 100%)',
                },
              }}
            >
              {isExported ? 'Shotlist Saved!' : 'Export Shotlist PDF'}
            </Button>
          </Box>
        </Box>

        {/* ══════════════════════════════════════════════════════════
            2. CINEMA PROMPT PRESET SELECTOR
            ══════════════════════════════════════════════════════════ */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            mb: 2.5,
          }}
        >
          {STORYBOARD_PROMPTS.map((prompt) => {
            const isSelected = prompt.id === selectedPromptId;
            return (
              <Chip
                key={prompt.id}
                label={`${prompt.title} (${prompt.genre})`}
                onClick={() => handleSelectPrompt(prompt.id)}
                sx={{
                  fontWeight: 800,
                  fontSize: '0.74rem',
                  cursor: 'pointer',
                  py: 0.8,
                  px: 0.6,
                  borderRadius: '14px',
                  bgcolor: isSelected ? 'rgba(6, 182, 212, 0.2)' : isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
                  color: isSelected ? '#38bdf8' : isDarkMode ? '#94a3b8' : '#64748b',
                  border: isSelected ? '1.5px solid #06b6d4' : isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
                  boxShadow: isSelected ? '0 4px 14px rgba(6, 182, 212, 0.3)' : 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'rgba(6, 182, 212, 0.15)',
                    transform: 'translateY(-1px)',
                  },
                }}
              />
            );
          })}
        </Box>

        {/* Active Prompt Prompt Box */}
        <Box
          sx={{
            bgcolor: isDarkMode ? '#020617' : '#f8fafc',
            borderRadius: '14px',
            p: 1.5,
            mb: 2.5,
            border: '1px solid rgba(6, 182, 212, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <AutoAwesomeRoundedIcon sx={{ color: '#06b6d4', fontSize: 18, flexShrink: 0 }} />
          <Typography
            sx={{
              fontFamily: 'ui-monospace, SFMono-Regular, monospace',
              fontSize: '0.75rem',
              color: isDarkMode ? '#94a3b8' : '#475569',
              lineHeight: 1.5,
            }}
          >
            <strong style={{ color: '#06b6d4' }}>Active Scenario Prompt:</strong> "{activePrompt.promptText}"
          </Typography>
        </Box>

        {/* Diffusion Generation Progress Bar (Visible while synthesizing) */}
        {isGenerating && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}>
              <Typography sx={{ color: '#06b6d4', fontSize: '0.68rem', fontWeight: 800, fontFamily: 'monospace' }}>
                SYNTHESIZING LATENT 3D STORYBOARD PLATES...
              </Typography>
              <Typography sx={{ color: '#06b6d4', fontSize: '0.68rem', fontWeight: 900, fontFamily: 'monospace' }}>
                {generationProgress}%
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                height: 4,
                bgcolor: 'rgba(6, 182, 212, 0.2)',
                borderRadius: '2px',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${generationProgress}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #06b6d4 0%, #3b82f6 50%, #8b5cf6 100%)',
                  boxShadow: '0 0 10px #06b6d4',
                  transition: 'width 0.1s linear',
                }}
              />
            </Box>
          </Box>
        )}

        {/* ══════════════════════════════════════════════════════════
            3. THREE STORYBOARD CAMERA PLATES GRID
            ══════════════════════════════════════════════════════════ */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {activePrompt.shots.map((shot, idx) => {
            const isSelected = selectedShotPlate === idx;
            return (
              <Box
                key={idx}
                onClick={() => handleShotClick(idx)}
                sx={{
                  borderRadius: '16px',
                  bgcolor: isDarkMode ? '#020617' : '#ffffff',
                  border: isSelected ? '2px solid #06b6d4' : isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
                  boxShadow: isSelected
                    ? '0 8px 24px rgba(6, 182, 212, 0.4)'
                    : '0 4px 14px rgba(0,0,0,0.06)',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  transform: isSelected ? 'scale(1.02)' : 'none',
                  '&:hover': {
                    borderColor: '#06b6d4',
                    transform: 'translateY(-2px) scale(1.02)',
                  },
                }}
              >
                {/* Widescreen 2.39:1 Cinema Plate Image */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: { xs: 150, sm: 160 },
                    bgcolor: '#000000',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src={shot.image}
                    alt={shot.number}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      opacity: isGenerating ? 0.35 : 1,
                      transition: 'opacity 0.3s ease, transform 0.4s ease',
                      '&:hover': {
                        transform: 'scale(1.04)',
                      },
                    }}
                  />

                  {/* 2.39:1 Letterbox Bars */}
                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 10, bgcolor: '#000000' }} />
                  <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 10, bgcolor: '#000000' }} />

                  {/* Top Header Tag */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 14,
                      left: 10,
                      bgcolor: 'rgba(2, 6, 23, 0.85)',
                      backdropFilter: 'blur(6px)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '6px',
                      px: 0.8,
                      py: 0.2,
                    }}
                  >
                    <Typography sx={{ color: '#38bdf8', fontSize: '0.6rem', fontWeight: 900, fontFamily: 'monospace' }}>
                      {shot.number}
                    </Typography>
                  </Box>

                  {/* Camera Icon Lens Badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 14,
                      right: 10,
                      bgcolor: 'rgba(2, 6, 23, 0.85)',
                      backdropFilter: 'blur(6px)',
                      border: '1px solid rgba(6, 182, 212, 0.4)',
                      borderRadius: '6px',
                      px: 0.8,
                      py: 0.2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.4,
                    }}
                  >
                    <CameraAltRoundedIcon sx={{ fontSize: 11, color: '#06b6d4' }} />
                    <Typography sx={{ color: '#ffffff', fontSize: '0.58rem', fontWeight: 800 }}>
                      {shot.aspect}
                    </Typography>
                  </Box>
                </Box>

                {/* Shot Direction & Script Notes */}
                <Box sx={{ p: 1.6 }}>
                  {/* Camera Specs Row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 1, flexWrap: 'wrap' }}>
                    <Chip
                      icon={<VideocamRoundedIcon sx={{ fontSize: '13px !important', color: '#06b6d4' }} />}
                      label={shot.framing}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.62rem',
                        fontWeight: 800,
                        bgcolor: 'rgba(6, 182, 212, 0.12)',
                        color: '#06b6d4',
                      }}
                    />
                    <Chip
                      label={shot.movement}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                        color: isDarkMode ? '#cbd5e1' : '#475569',
                      }}
                    />
                  </Box>

                  {/* Lighting note */}
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: '#f59e0b',
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      mb: 0.6,
                    }}
                  >
                    💡 Light: {shot.lighting}
                  </Typography>

                  {/* Script dialog or scene direction */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.74rem',
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                      lineHeight: 1.45,
                      fontStyle: 'italic',
                    }}
                  >
                    "{shot.scriptNote}"
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Pro-Tip Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 2,
            pt: 1.5,
            borderTop: '1px solid rgba(255,255,255,0.06)',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: isDarkMode ? '#94a3b8' : '#64748b',
              fontSize: '0.72rem',
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
            }}
          >
            <span>💡</span>
            Click on any storyboard plate above to preview camera framing on the 3D smartphone stage.
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: '#06b6d4',
              fontWeight: 800,
              fontSize: '0.72rem',
            }}
          >
            Filmhook Aether 1.0 Pro Engine • 4K Frame Diffusion
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default FilmhookStoryboardGenerator;
